import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';

import {
  AddressInformationResponse,
  AddUserToOrganizationRequest,
  ContractInformationRequest,
  ContactInformationRequest,
  ContactInformationResponse,
  CreateDepartmentRequest,
  CreateDepartmentResponse,
  CreateOrganizationRequest,
  CreateOrganizationResponse,
  ExportFormatEnumDto,
  ListOrganizationsResponse,
  OrganizationResponse,
  RoleEnumDto,
  StackNumberingEnumDto,
  StateEnumDto,
  UpdateAddressInformationRequest,
  GetStackResponse,
  UserInfoResponse,
  ListStacksResponse,
  DepartmentResponse,
  AddDepartmentUserRequest,
  UserModelDto,
  LoginRequest,
  LoginResponse,
  ListDepartmentsResponse,
  UpdateOrganizationUserRequest,
  UpdateDepartmentUserRequest,
  GetMeasurementCardListResponse,
  MeasurementCardInformationRequest,
  BulkMeasurementCardInformationRequest,
  BulkUpdateMeasurementCardResponse,
  AddEmailToMailingListRequest,
  RemoveEmailFromMailingListRequest,
  AddAdministratorRequest,
  AddOrganizationContactRequest,
  UpdateOrganizationContactRequest,
  OrganizationLogoResponse,
  StackMapListResponse,
  StackMapInfoResponse,
  StackStatisticsResponse,
  StackFilterOptionsResponse,
  MeasurementCardResponse,
  CreateSawmillResponse,
  CreateSawmillRequest,
  RenameSawmillRequest,
  SawmillResponsePaginatedResponse,
  AssignStacksToSawmillRequest,
  AddSawmillUserRequest,
  UpdateSawmillUserRequest,
  SawmillUserInfoResponse,
  RefreshTokenResponse,
  RefreshRequest,
  ContractInformationResponse,
  UpdateDepartmentCustomerNumberRequest,
  ListOrganizationsDetailedResponse,
  DownloadArchiveRequest,
  DownloadAccumulatedRequest,
  DownloadAccumulatedFromFilterRequest,
  StacksQueryPagedRequest,
  StacksBySawmillQueryPagedRequest,
  StackFilterOptionsQueryRequest,
  StackStatisticsQueryRequest,
} from '../.generated/api';
import { SessionStorageService } from './sessionStorageService';

import qs from 'qs';
import { getTokenData } from 'helpers/decodeToken';
import { localizeAuthorizationError } from './localizeAuthorizationError';

export interface GetSawmillListFilter {
  organizationId: string;
  page: number;
  pageSize: number;
}

export interface StackFileLink {
  linkId: string;
  stackId: string;
  token: string;
  createdBy: string;
  intendedFor: string;
  expires: string;
  revoked: boolean;
}

export interface OrganizationListQuery {
  page: number;
  pageSize: number;
  name?: string;
  active?: boolean;
  contractStatus?: 'Expired' | 'ExpiringSoon' | 'Active';
}

const axiosRequestConfig = (): AxiosRequestConfig => {
  const token = SessionStorageService.getToken();
  return {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    paramsSerializer: (params) => {
      return qs.stringify(params, { arrayFormat: 'repeat' });
    },
  };
};

let refreshPromise: Promise<void> | undefined; // refresh operation in progress
const expirationOffsetInMinutes = 10;

async function refreshTokenIfExpired(): Promise<void> {
  // console.log('refresh if expired');
  const token = SessionStorageService.getToken();
  const refreshToken = SessionStorageService.getRefreshToken();
  if (token && refreshToken) {
    const { username, expirationTimestamp } = getTokenData(token);
    if (username && expirationTimestamp) {
      const expirationDate = new Date(expirationTimestamp * 1000 /* s => ms */);
      const refDate = new Date();
      refDate.setMinutes(refDate.getMinutes() + expirationOffsetInMinutes); // refresh x minutes before expiration
      // console.log('compare expiration dates', expirationDate, refDate);
      if (expirationDate < refDate) {
        try {
          const response = await ApiService.refreshToken(
            username,
            refreshToken,
          );
          SessionStorageService.setToken(response.data.token);
          SessionStorageService.setRefreshToken(response.data.refreshToken);
        } catch (error) {
          console.warn('Refresh token failed', error);
        }
      }
    }
  }
}

// create axios instance that checks for token expiration.
const authAxios = axios.create();
authAxios.interceptors.request.use(async (config: AxiosRequestConfig) => {
  if (refreshPromise) {
    // reusing current refresh promise will prevent multiple refresh calls from being sent simultaniously.
    // also multiple Api calls may wait on the same refresh token call.
    // console.log('reuse promise', refreshPromise);
    await refreshPromise;
  } else {
    const promise = refreshTokenIfExpired();
    refreshPromise = promise;
    await promise;
    refreshPromise = undefined;
  }
  const refreshedToken = SessionStorageService.getToken();
  if (refreshedToken) {
    config.headers = config.headers ?? {};
    config.headers.Authorization = `Bearer ${refreshedToken}`;
  }
  return config;
});

authAxios.interceptors.response.use(
  (response) => response,
  (error: unknown) => Promise.reject(localizeAuthorizationError(error)),
);

export class ApiService {
  static baseUrl = 'api/v1';

  static refreshTokenIfExpired(): Promise<void> {
    return refreshTokenIfExpired();
  }

  static async getUserInfo(): Promise<AxiosResponse<UserInfoResponse>> {
    return authAxios.get<UserInfoResponse>(
      `${ApiService.baseUrl}/user-info`,
      axiosRequestConfig(),
    );
  }

  static async getSawmillUserInfo(): Promise<
    AxiosResponse<SawmillUserInfoResponse>
  > {
    return authAxios.get<SawmillUserInfoResponse>(
      `${ApiService.baseUrl}/user-info/sawmill`,
      axiosRequestConfig(),
    );
  }

  static async getOrganizationMeasurementCard(
    orgId: string,
  ): Promise<AxiosResponse<MeasurementCardResponse>> {
    return authAxios.get<MeasurementCardResponse>(
      `${ApiService.baseUrl}/organization/${orgId}/measurement-card`,
      axiosRequestConfig(),
    );
  }

  static async getMeasurementCardList(): Promise<
    AxiosResponse<GetMeasurementCardListResponse>
  > {
    return authAxios.get<GetMeasurementCardListResponse>(
      `${ApiService.baseUrl}/measurement-card/list`,
      axiosRequestConfig(),
    );
  }

  static async getOrganizations(): Promise<
    AxiosResponse<ListOrganizationsResponse>
  > {
    const query = new URLSearchParams({ page: '1', pageSize: '100' });
    return authAxios.get<ListOrganizationsResponse>(
      `${ApiService.baseUrl}/organization/list?${query.toString()}`,
      axiosRequestConfig(),
    );
  }

  static async getOrganizationsDetailed(
    query: OrganizationListQuery,
  ): Promise<AxiosResponse<ListOrganizationsDetailedResponse>> {
    const searchParams = new URLSearchParams({
      page: query.page.toString(),
      pageSize: query.pageSize.toString(),
    });

    if (query.name) {
      searchParams.set('name', query.name);
    }

    if (query.active !== undefined) {
      searchParams.set('active', query.active.toString());
    }

    if (query.contractStatus) {
      searchParams.set('contractStatus', query.contractStatus);
    }

    return authAxios.get<ListOrganizationsDetailedResponse>(
      `${
        ApiService.baseUrl
      }/organization/list/detailed?${searchParams.toString()}`,
      axiosRequestConfig(),
    );
  }

  static async getOrganizationById(
    organizationId: string,
  ): Promise<AxiosResponse<OrganizationResponse>> {
    return authAxios.get<OrganizationResponse>(
      `${ApiService.baseUrl}/organization/${organizationId}`,
      axiosRequestConfig(),
    );
  }

  static async getCurrentContract(
    orgId: string,
  ): Promise<AxiosResponse<ContractInformationResponse>> {
    return authAxios.get<ContractInformationResponse>(
      `${ApiService.baseUrl}/organization/${orgId}/contract-information`,
      axiosRequestConfig(),
    );
  }

  static async getNextContract(
    orgId: string,
  ): Promise<AxiosResponse<ContractInformationResponse>> {
    return authAxios.get<ContractInformationResponse>(
      `${ApiService.baseUrl}/organization/${orgId}/contract-information/next`,
      axiosRequestConfig(),
    );
  }

  static async updateNextContract(
    orgId: string,
    contract: ContractInformationRequest,
  ): Promise<AxiosResponse> {
    return authAxios.put(
      `${ApiService.baseUrl}/organization/${orgId}/contract-information/next`,
      contract,
      axiosRequestConfig(),
    );
  }

  static async createOrganization(
    name: string,
    country: string,
    groupCode: string,
  ): Promise<AxiosResponse<CreateOrganizationResponse>> {
    const createOrg: CreateOrganizationRequest = { name, country, groupCode };
    return authAxios.post<CreateOrganizationResponse>(
      `${ApiService.baseUrl}/organization`,
      createOrg,
      axiosRequestConfig(),
    );
  }

  static async updateContractInformation(
    organizationId: string,
    contractInformation: ContractInformationRequest,
  ): Promise<AxiosResponse> {
    return authAxios.put(
      `${ApiService.baseUrl}/organization/${organizationId}/contract-information`,
      contractInformation,
      axiosRequestConfig(),
    );
  }

  static async addUserToOrganization(
    organizationId: string,
    user: {
      name: string;
      userEmail: string;
      phone: string;
      state: StateEnumDto;
      roles: RoleEnumDto[];
      enableAutoEmails: boolean;
    },
  ): Promise<AxiosResponse> {
    const addUserToOrg: AddUserToOrganizationRequest = {
      ...user,
    };

    return authAxios.post(
      `${ApiService.baseUrl}/organization/${organizationId}/user`,
      addUserToOrg,
      axiosRequestConfig(),
    );
  }

  static async editUserInOrganization(
    organizationId: string,
    email: string,
    name: string,
    phone: string,
    roles: RoleEnumDto[],
    enableAutoEmails: boolean,
  ): Promise<AxiosResponse> {
    const editUserInOrg: UpdateOrganizationUserRequest = {
      name,
      phone,
      roles,
      enableAutoEmails,
    };
    return authAxios.put(
      `${ApiService.baseUrl}/organization/${organizationId}/user`,
      editUserInOrg,
      { ...axiosRequestConfig(), params: { email } },
    );
  }

  static async addOrganizationAdmin(
    organizationId: string,
    email: string,
    name: string,
    phone: string,
  ): Promise<AxiosResponse> {
    const addAdminRequest: AddAdministratorRequest = {
      email,
      name,
      phone,
    };
    return authAxios.post(
      `${ApiService.baseUrl}/organization/${organizationId}/add-administrator`,
      addAdminRequest,
      axiosRequestConfig(),
    );
  }

  static async deactivateUserInOrganization(
    organizationId: string,
    email: string,
  ): Promise<AxiosResponse> {
    return authAxios.put(
      `${ApiService.baseUrl}/organization/${organizationId}/user/deactivate`,
      null,
      { ...axiosRequestConfig(), params: { email } },
    );
  }

  static async activateUserInOrganization(
    organizationId: string,
    email: string,
  ): Promise<AxiosResponse> {
    return authAxios.put(
      `${ApiService.baseUrl}/organization/${organizationId}/user/activate`,
      null,
      { ...axiosRequestConfig(), params: { email } },
    );
  }

  static async resendConfirmationEmailInOrganization(
    organizationId: string,
    email: string,
  ): Promise<AxiosResponse> {
    return authAxios.post(
      `${ApiService.baseUrl}/organization/${organizationId}/resend-confirmation-email`,
      null,
      { ...axiosRequestConfig(), params: { email } },
    );
  }

  static async createSawmill(
    organizationId: string,
    name: string,
  ): Promise<AxiosResponse<CreateSawmillResponse>> {
    const createSawmillRequest: CreateSawmillRequest = {
      organizationId,
      name,
    };
    return authAxios.post<CreateSawmillResponse>(
      `${ApiService.baseUrl}/sawmill`,
      createSawmillRequest,
      axiosRequestConfig(),
    );
  }

  static async renameSawmill(
    sawmillId: string,
    name: string,
  ): Promise<AxiosResponse> {
    const renameSawmillRequest: RenameSawmillRequest = {
      name,
    };
    return authAxios.put(
      `${ApiService.baseUrl}/sawmill/${sawmillId}/rename`,
      renameSawmillRequest,
      axiosRequestConfig(),
    );
  }

  static async deleteSawmill(sawmillId: string): Promise<AxiosResponse> {
    return authAxios.delete(
      `${ApiService.baseUrl}/sawmill/${sawmillId}`,
      axiosRequestConfig(),
    );
  }

  static async getSawmillList(
    filter: GetSawmillListFilter,
  ): Promise<AxiosResponse<SawmillResponsePaginatedResponse>> {
    return authAxios.get<SawmillResponsePaginatedResponse>(
      `${ApiService.baseUrl}/sawmill/list`,
      { ...axiosRequestConfig(), params: { ...filter } },
    );
  }

  static async assignStacksToSawmill(
    sawmillId: string,
    stackIds: string[],
  ): Promise<AxiosResponse> {
    const assignStacksToSawmillRequest: AssignStacksToSawmillRequest = {
      stackIds,
    };
    return authAxios.post(
      `${ApiService.baseUrl}/sawmill/${sawmillId}/assign-stacks`,
      assignStacksToSawmillRequest,
      axiosRequestConfig(),
    );
  }

  static async addSawmillUser(
    sawmillId: string,
    sawmillUser: AddSawmillUserRequest,
  ): Promise<AxiosResponse> {
    return authAxios.post(
      `${ApiService.baseUrl}/sawmill/${sawmillId}/user`,
      sawmillUser,
      axiosRequestConfig(),
    );
  }

  static async updateSawmillUser(
    sawmillId: string,
    sawmillUserId: string,
    sawmillUser: UpdateSawmillUserRequest,
  ): Promise<AxiosResponse> {
    return authAxios.put(
      `${ApiService.baseUrl}/sawmill/${sawmillId}/user/${sawmillUserId}`,
      sawmillUser,
      axiosRequestConfig(),
    );
  }

  static async removeSawmillUser(
    sawmillId: string,
    sawmillUserId: string,
  ): Promise<AxiosResponse> {
    return authAxios.delete(
      `${ApiService.baseUrl}/sawmill/${sawmillId}/user/${sawmillUserId}`,
      axiosRequestConfig(),
    );
  }

  static async addUserToDepartment(
    departmentId: string,
    user: UserModelDto,
  ): Promise<AxiosResponse> {
    const addUserToDept: AddDepartmentUserRequest = {
      name: user.name,
      email: user.userEmail,
      phone: user.phone,
      roles: user.roles,
      enableAutoEmails: user.enableAutoEmails,
    };

    return authAxios.post(
      `${ApiService.baseUrl}/department/${departmentId}/user`,
      addUserToDept,
      axiosRequestConfig(),
    );
  }

  static async editUserInDeptment(
    deptId: string,
    email: string,
    name: string,
    phone: string,
    roles: RoleEnumDto[],
    enableAutoEmails: boolean,
  ): Promise<AxiosResponse> {
    const editUserInDept: UpdateDepartmentUserRequest = {
      name,
      phone,
      roles,
      enableAutoEmails,
    };

    return authAxios.put(
      `${ApiService.baseUrl}/department/${deptId}/user`,
      editUserInDept,
      { ...axiosRequestConfig(), params: { email } },
    );
  }

  static async deactivateUserInDepartment(
    departmentId: string,
    email: string,
  ): Promise<AxiosResponse> {
    return authAxios.put(
      `${ApiService.baseUrl}/department/${departmentId}/user/deactivate`,
      null,
      { ...axiosRequestConfig(), params: { email } },
    );
  }

  static async activateUserInDepartment(
    departmentId: string,
    email: string,
  ): Promise<AxiosResponse> {
    return authAxios.put(
      `${ApiService.baseUrl}/department/${departmentId}/user/activate`,
      null,
      { ...axiosRequestConfig(), params: { email } },
    );
  }

  static async resendConfirmationEmailInDepartment(
    departmentId: string,
    email: string,
  ): Promise<AxiosResponse> {
    return authAxios.post(
      `${ApiService.baseUrl}/department/${departmentId}/resend-confirmation-email`,
      null,
      { ...axiosRequestConfig(), params: { email } },
    );
  }

  static async updateMeasurementCardInformation(
    organizationId: string,
    measurementCardId: string,
    stackNumbering: StackNumberingEnumDto,
    exportFormat: ExportFormatEnumDto,
  ): Promise<AxiosResponse> {
    const request: MeasurementCardInformationRequest = {
      measurementCardId,
      stackNumbering,
      exportFormat,
    };

    return authAxios.put(
      `${ApiService.baseUrl}/organization/${organizationId}/measurement-card-information`,
      request,
      axiosRequestConfig(),
    );
  }

  static async bulkUpdateMeasurementCardInformation(
    organizationIds: string[],
    measurementCardId: string,
    stackNumbering: StackNumberingEnumDto,
    exportFormat: ExportFormatEnumDto,
  ): Promise<AxiosResponse<BulkUpdateMeasurementCardResponse>> {
    const request: BulkMeasurementCardInformationRequest = {
      organizationIds,
      measurementCardId,
      stackNumbering,
      exportFormat,
    };

    return authAxios.post<BulkUpdateMeasurementCardResponse>(
      `${ApiService.baseUrl}/organization/bulk-update-measurement-card-information`,
      request,
      axiosRequestConfig(),
    );
  }

  static async createDeptment(
    organizationId: string,
    name: string,
    departmentNumber?: number,
  ): Promise<AxiosResponse<CreateDepartmentResponse>> {
    const createDept: CreateDepartmentRequest = {
      name,
      organizationId,
      departmentNumber,
    };

    return authAxios.post<CreateDepartmentResponse>(
      `${ApiService.baseUrl}/department`,
      createDept,
      axiosRequestConfig(),
    );
  }

  static async getDepartmentById(
    departmentId: string,
  ): Promise<AxiosResponse<DepartmentResponse>> {
    return authAxios.get<DepartmentResponse>(
      `${ApiService.baseUrl}/department/${departmentId}`,
      axiosRequestConfig(),
    );
  }

  static async addDepartmentAdmin(
    departmentId: string,
    email: string,
    name: string,
    phone: string,
  ): Promise<AxiosResponse> {
    const addAdminRequest: AddAdministratorRequest = {
      email,
      name,
      phone,
    };
    return authAxios.post(
      `${ApiService.baseUrl}/department/${departmentId}/add-administrator`,
      addAdminRequest,
      axiosRequestConfig(),
    );
  }

  static async updateOrganization(
    organizationId: string,
    name: string,
    groupCode: string,
    preferLinksInEmail: boolean,
  ): Promise<AxiosResponse> {
    return authAxios.put(
      `${ApiService.baseUrl}/organization/${organizationId}/update`,
      { name, groupCode, preferLinksInEmail },
      axiosRequestConfig(),
    );
  }

  static async prepareForest(
    organizationId: string,
    regionCode: number,
    inspectorateCode: number,
  ): Promise<AxiosResponse> {
    return authAxios.put(
      `${ApiService.baseUrl}/organization/${organizationId}/prepare-forest`,
      { regionCode, inspectorateCode },
      axiosRequestConfig(),
    );
  }

  static async updateDepartmentInfo(
    departmentId: string,
    name: string,
    departmentNumber?: number,
  ): Promise<AxiosResponse> {
    return authAxios.put(
      `${ApiService.baseUrl}/department/${departmentId}/info`,
      { name, departmentNumber },
      axiosRequestConfig(),
    );
  }

  static async updateDepartmentCustomerNumber(
    departmentId: string,
    customerNumber: UpdateDepartmentCustomerNumberRequest,
  ): Promise<AxiosResponse> {
    return authAxios.put(
      `${ApiService.baseUrl}/department/${departmentId}/customer-number`,
      customerNumber,
      axiosRequestConfig(),
    );
  }

  static async updateOrganizationAddressDetails(
    organizationId: string,
    organizationAddress: AddressInformationResponse,
  ): Promise<AxiosResponse> {
    const request: UpdateAddressInformationRequest = {
      ...organizationAddress,
    };

    return authAxios.put(
      `${ApiService.baseUrl}/organization/${organizationId}/address-information`,
      request,
      axiosRequestConfig(),
    );
  }

  static async getOrganizationLogo(
    organizationId: string,
  ): Promise<AxiosResponse<OrganizationLogoResponse>> {
    return authAxios.get<OrganizationLogoResponse>(
      `${ApiService.baseUrl}/organization/${organizationId}/logo`,
      axiosRequestConfig(),
    );
  }

  static async updateOrganizationLogo(
    organizationId: string,
    file: string | Blob,
  ): Promise<AxiosResponse> {
    const formData = new FormData();

    formData.append('file', file);

    return authAxios.put(
      `${ApiService.baseUrl}/organization/${organizationId}/logo`,
      formData,
      axiosRequestConfig(),
    );
  }

  static async updateOrganizationContactDetails(
    organizationId: string,
    organizationContactInformation: ContactInformationResponse,
  ): Promise<AxiosResponse> {
    const updateOrganization: ContactInformationRequest = {
      ...organizationContactInformation,
    };

    return authAxios.put(
      `${ApiService.baseUrl}/organization/${organizationId}/contact-information`,
      updateOrganization,
      axiosRequestConfig(),
    );
  }

  static async getStackById(
    stackId: string,
  ): Promise<AxiosResponse<GetStackResponse>> {
    return authAxios.get<GetStackResponse>(
      `${ApiService.baseUrl}/stack/${stackId}`,
      axiosRequestConfig(),
    );
  }

  static async getStackFileLinks(
    stackId: string,
  ): Promise<AxiosResponse<StackFileLink[]>> {
    return authAxios.get<StackFileLink[]>(
      `${ApiService.baseUrl}/stack/${stackId}/file-links`,
      axiosRequestConfig(),
    );
  }

  static async generateStackFileLink(
    stackId: string,
  ): Promise<AxiosResponse<StackFileLink>> {
    return authAxios.post<StackFileLink>(
      `${ApiService.baseUrl}/stack/${stackId}/file-link`,
      undefined,
      axiosRequestConfig(),
    );
  }

  static async revokeStackFileLink(
    stackId: string,
    linkId: string,
  ): Promise<AxiosResponse> {
    return authAxios.delete(
      `${ApiService.baseUrl}/stack/${stackId}/file-links/${linkId}`,
      axiosRequestConfig(),
    );
  }

  static async downloadStackArchive(
    stackIds: string[],
    onDownloadProgress?: (event: ProgressEvent) => void,
  ): Promise<AxiosResponse> {
    const request: DownloadArchiveRequest = { stackIds };
    return authAxios.put(
      `${ApiService.baseUrl}/stack/download-archive`,
      request,
      {
        ...axiosRequestConfig(),
        responseType: 'blob',
        onDownloadProgress,
      },
    );
  }

  static async downloadAccumulatedCSV(
    measurementCardId: string,
    stackIds: string[],
    onDownloadProgress?: (event: ProgressEvent) => void,
  ): Promise<AxiosResponse> {
    const request: DownloadAccumulatedRequest = { measurementCardId, stackIds };
    return authAxios.put(
      `${ApiService.baseUrl}/stack/download-accumulated-csv`,
      request,
      {
        ...axiosRequestConfig(),
        responseType: 'blob',
        onDownloadProgress,
      },
    );
  }

  static async downloadAccumulatedCSVFromFilter(
    request: DownloadAccumulatedFromFilterRequest,
    onDownloadProgress?: (event: ProgressEvent) => void,
  ): Promise<AxiosResponse> {
    return authAxios.put(
      `${ApiService.baseUrl}/stack/download-accumulated-csv-from-filter`,
      request,
      {
        ...axiosRequestConfig(),
        responseType: 'blob',
        onDownloadProgress,
      },
    );
  }

  static async deleteStack(stackId: string): Promise<AxiosResponse> {
    return authAxios.delete(
      `${ApiService.baseUrl}/stack/${stackId}`,
      axiosRequestConfig(),
    );
  }

  static async bulkRegenerateFiles(stackIds: string[]): Promise<AxiosResponse> {
    return authAxios.post(
      `${ApiService.baseUrl}/stack/bulk-regenerate-files`,
      { stackIds },
      axiosRequestConfig(),
    );
  }

  static async getStackList(
    request: StacksQueryPagedRequest,
  ): Promise<AxiosResponse<ListStacksResponse>> {
    return authAxios.put<ListStacksResponse>(
      `${ApiService.baseUrl}/stack/list`,
      request,
      {
        ...axiosRequestConfig(),
      },
    );
  }

  static async getStackMapList(
    request: StacksQueryPagedRequest,
  ): Promise<AxiosResponse<StackMapListResponse>> {
    return authAxios.put<StackMapListResponse>(
      `${ApiService.baseUrl}/stack/map-list`,
      request,
      {
        ...axiosRequestConfig(),
      },
    );
  }

  static async getSawmillStackList(
    request: StacksBySawmillQueryPagedRequest,
  ): Promise<AxiosResponse<ListStacksResponse>> {
    return authAxios.put<ListStacksResponse>(
      `${ApiService.baseUrl}/stack/list/sawmill`,
      request,
      {
        ...axiosRequestConfig(),
      },
    );
  }

  static async getSawmillStackMapList(
    request: StacksBySawmillQueryPagedRequest,
  ): Promise<AxiosResponse<StackMapListResponse>> {
    return authAxios.put<StackMapListResponse>(
      `${ApiService.baseUrl}/stack/map-list/sawmill`,
      request,
      {
        ...axiosRequestConfig(),
      },
    );
  }

  static async getStackMapInfoById(
    stackId: string,
  ): Promise<AxiosResponse<StackMapInfoResponse>> {
    return authAxios.get<StackMapInfoResponse>(
      `${ApiService.baseUrl}/stack/${stackId}/map-info`,
      {
        ...axiosRequestConfig(),
      },
    );
  }

  static async getStackFilterOptions(
    request: StackFilterOptionsQueryRequest,
  ): Promise<AxiosResponse<StackFilterOptionsResponse>> {
    return authAxios.put<StackFilterOptionsResponse>(
      `${ApiService.baseUrl}/stack/filter-options`,
      request,
      {
        ...axiosRequestConfig(),
      },
    );
  }

  static async login(
    username: string,
    password: string,
  ): Promise<AxiosResponse<LoginResponse>> {
    const loginRequest: LoginRequest = { username, password };
    return axios.post<LoginResponse>(
      `${ApiService.baseUrl}/auth/login`,
      loginRequest,
    );
  }

  static async entraLogin(
    idToken: string,
  ): Promise<AxiosResponse<LoginResponse>> {
    return axios.post<LoginResponse>(`${ApiService.baseUrl}/auth/entra-login`, {
      idToken,
    });
  }

  static async refreshToken(
    username: string,
    refreshToken: string,
  ): Promise<AxiosResponse<RefreshTokenResponse>> {
    const refreshRequest: RefreshRequest = { userId: username, refreshToken };

    return axios.post<LoginResponse>(
      `${ApiService.baseUrl}/auth/refresh-token`,
      refreshRequest,
    );
  }

  static async getDepartmentList(): Promise<
    AxiosResponse<ListDepartmentsResponse>
  > {
    return authAxios.get<ListDepartmentsResponse>(
      `${ApiService.baseUrl}/department/list`,
      axiosRequestConfig(),
    );
  }

  static async confirmAccount(
    email: string,
    password: string,
    token: string,
  ): Promise<AxiosResponse> {
    const confirmAccount = {
      email,
      password,
      token,
    };
    return axios.post(
      `${ApiService.baseUrl}/auth/confirm-account`,
      confirmAccount,
    );
  }

  static async forgotPassword(username: string): Promise<AxiosResponse> {
    return axios.post(`${ApiService.baseUrl}/auth/forgot-password`, {
      email: username,
    });
  }

  static async resetPassword(
    email: string,
    password: string,
    token: string,
  ): Promise<AxiosResponse> {
    const resetPassword = {
      email,
      newPassword: password,
      token,
    };
    return axios.post(
      `${ApiService.baseUrl}/auth/reset-password`,
      resetPassword,
    );
  }

  static async getStackStatistics(
    request: StackStatisticsQueryRequest,
  ): Promise<AxiosResponse<StackStatisticsResponse>> {
    return authAxios.put<StackStatisticsResponse>(
      `${ApiService.baseUrl}/stack/statistics`,
      request,
      {
        ...axiosRequestConfig(),
      },
    );
  }

  static async addEmailToDepartmentMailingList(
    departmentId: string,
    email: string,
  ): Promise<AxiosResponse> {
    const request: AddEmailToMailingListRequest = { email };
    return authAxios.put(
      `${ApiService.baseUrl}/department/${departmentId}/add-email-to-mailing-list`,
      request,
      axiosRequestConfig(),
    );
  }

  static async removeEmailFromDepartmentMailingList(
    departmentId: string,
    email: string,
  ): Promise<AxiosResponse> {
    const request: RemoveEmailFromMailingListRequest = { email };
    return authAxios.put(
      `${ApiService.baseUrl}/department/${departmentId}/remove-email-from-mailing-list`,
      request,
      axiosRequestConfig(),
    );
  }

  static async addOrganizationContact(
    organizationId: string,
    name: string | undefined,
    email: string | undefined,
    phone: string | undefined,
  ): Promise<AxiosResponse> {
    const request: AddOrganizationContactRequest = {
      email,
      name,
      phone,
    };

    return authAxios.post(
      `${ApiService.baseUrl}/organization/${organizationId}/contact`,
      request,
      axiosRequestConfig(),
    );
  }

  static async updateOrganizationContact(
    organizationId: string,
    contactId: string,
    name: string | undefined,
    email: string | undefined,
    phone: string | undefined,
  ): Promise<AxiosResponse> {
    const request: UpdateOrganizationContactRequest = {
      name,
      email,
      phone,
    };

    return authAxios.put(
      `${ApiService.baseUrl}/organization/${organizationId}/contact/${contactId}`,
      request,
      axiosRequestConfig(),
    );
  }

  static async deleteOrganizationContact(
    organizationId: string,
    contactId: string,
  ): Promise<AxiosResponse> {
    return authAxios.delete(
      `${ApiService.baseUrl}/organization/${organizationId}/contact/${contactId}`,
      axiosRequestConfig(),
    );
  }

  static async deleteOrganization(
    organizationId: string,
  ): Promise<AxiosResponse> {
    return authAxios.delete(
      `${ApiService.baseUrl}/organization/${organizationId}`,
      axiosRequestConfig(),
    );
  }

  static async addDepartmentContact(
    departmentId: string,
    name: string | undefined,
    email: string | undefined,
    phone: string | undefined,
  ): Promise<AxiosResponse> {
    const request: AddOrganizationContactRequest = {
      email,
      name,
      phone,
    };

    return authAxios.post(
      `${ApiService.baseUrl}/department/${departmentId}/contact`,
      request,
      axiosRequestConfig(),
    );
  }

  static async updateDepartmentContact(
    departmentId: string,
    contactId: string,
    name: string | undefined,
    email: string | undefined,
    phone: string | undefined,
  ): Promise<AxiosResponse> {
    const request: UpdateOrganizationContactRequest = {
      name,
      email,
      phone,
    };

    return authAxios.put(
      `${ApiService.baseUrl}/department/${departmentId}/contact/${contactId}`,
      request,
      axiosRequestConfig(),
    );
  }

  static async deleteDepartmentContact(
    departmentId: string,
    contactId: string,
  ): Promise<AxiosResponse> {
    return authAxios.delete(
      `${ApiService.baseUrl}/department/${departmentId}/contact/${contactId}`,
      axiosRequestConfig(),
    );
  }
}
